import { useTranslation } from 'react-i18next';
import { LoungeLogo } from './LoungeLogo';
import type { API } from '@/api';
import { RESERVABLE_TIME_UNIT_IN_MINUTES, SPACE_TYPE } from '@/constants/space';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { useFloorFormat } from '@/hooks/useFloorFormat';
import { useNameFormat } from '@/hooks/useNameFormat';

// 共有端末用の予約データ
type SharedDeviceReservation =
  API['POST']['CreateSharedDeviceReservation']['response']['reservation'];

// 個人端末用の予約データ
type PersonalDeviceReservation =
  API['POST']['CreateReservation']['response']['reservation'];

// 共通フィールドの型（両方のレスポンスで共通）
type CommonReservation = SharedDeviceReservation | PersonalDeviceReservation;

interface ReservationCardProps {
  reservation: CommonReservation;
  onOpenFloorMap?: () => void;
  floorMapImagePath?: string | null;
}

/**
 * 予約情報カードコンポーネント
 */
export const ReservationCard = ({
  reservation,
  onOpenFloorMap,
  floorMapImagePath,
}: ReservationCardProps) => {
  const { t, i18n } = useTranslation();
  const { formatDateWithWeekdayLong, formatTimeRangeWithDuration } =
    useDateTimeFormat();
  const { formatFloor } = useFloorFormat();
  const { formatName } = useNameFormat();

  return (
    <div className="f6__card">
      <dl className="f6__card-list">
        {/* 名前 */}
        <div className="f6__card-row">
          <div className="f6__card-name">
            {formatName(reservation.last_name, reservation.first_name)}
          </div>
        </div>

        {/* 日付 */}
        <div className="f6__card-row">
          <div className="f6__card-desc f6__card-date">
            {formatDateWithWeekdayLong(reservation.date)}
          </div>
        </div>

        {/* 時間 */}
        <div className="f6__card-row">
          <div className="f6__card-desc f6__card-time">
            {formatTimeRangeWithDuration(
              reservation.start_time,
              reservation.end_time,
              RESERVABLE_TIME_UNIT_IN_MINUTES[SPACE_TYPE.SHOWER_ROOM],
            )}
          </div>
        </div>

        {/* 空港 + MAPボタン */}
        <div className="f6__card-row">
          <div className="f6__card-desc f6__card-airport">
            {t(`common.airport.${reservation.airport}`)}
            {floorMapImagePath && (
              <button
                className="f6__btn f6__btn--map js-open-dialog"
                type="button"
                onClick={onOpenFloorMap}
              >
                {t('shareDevice.complete.floorMapButton')}
              </button>
            )}
          </div>
        </div>

        {/* ターミナル */}
        <div className="f6__card-row">
          <div className="f6__card-desc f6__card-terminal">
            {i18n.language === 'en'
              ? `${t(`common.terminal.${reservation.terminal}`)} (${t(`common.flightType.${reservation.flight_type}`)})`
              : `${t(`common.terminal.${reservation.terminal}`)} ${t(`common.flightType.${reservation.flight_type}`)}`}
          </div>
        </div>

        {/* ラウンジ */}
        <div className="f6__card-row f6__card-row--lounge">
          <div className="f6__card-desc f6__card-lounge">
            <div className="f6__lounge">
              <LoungeLogo
                lounge={reservation.lounge}
                className="f6__lounge-logo"
                altText={t(`common.lounge.${reservation.lounge}`)}
              />
              <div className="f6__lounge-floor">
                {formatFloor(reservation.floor, 'shareDevice.complete.floor')}
              </div>
            </div>
          </div>
        </div>

        {/* 予約番号 */}
        <div className="f6__card-row">
          <div className="f6__card-term">
            {t('shareDevice.complete.reservationNumber')}
          </div>
          <div className="f6__card-desc f6__card-reservation">
            <span className="f6__code">{reservation.reservation_no}</span>
          </div>
        </div>

        {/* シャワールーム番号 */}
        <div className="f6__card-row f6__card-row--room">
          <div className="f6__card-desc f6__card-room">
            <div className="f6__room">
              <div className="f6__room-img">
                <img
                  className="f6__room-icon"
                  src="/F_6_Showerroom.png"
                  alt={t('common.altText.showerRoom')}
                />
              </div>
              <div className="f6__room-no">
                {t('shareDevice.complete.spaceNo')}
                <span className="f6__room-no-number">
                  {reservation.space_no}
                </span>
              </div>
            </div>
          </div>
        </div>
      </dl>
    </div>
  );
};
